import { gql } from "@apollo/client";
import { Alert, Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { useProjectChangeState } from "../useProjectChangeState.js";
import { ProjectOpportunityAlertFragment } from "./ProjectOpportunityAlert.generated.js";

interface Props {
  project: ProjectOpportunityAlertFragment;
}

export const ProjectOpportunityAlert = ({ project }: Props) => {
  const { t } = useTranslate(["ProjectOverview", "RequestOverview", "Global"]);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const { handleStateChange } = useProjectChangeState({
    projectId: project.id,
    projectState: project.state,
    confirmProcessRef,
  });

  const hasExactlyOneIncomingRequest =
    project.incomingQuoteRequests.length === 1;

  if (!hasExactlyOneIncomingRequest) return null;

  const request = project.incomingQuoteRequests[0];
  const havePublishedQuotes = request.viewerRecipientNumPublishedQuotes > 0;

  if (request.status === "WITHDRAWN") {
    return (
      <>
        <Alert
          variant="outlined"
          severity="warning"
          action={
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={async () => {
                await handleStateChange("CONTRACTEE_DECLINE");
              }}
            >
              {t("Close opportunity", { ns: "ProjectOverview" })}
            </Button>
          }
        >
          {t("The customer has withdrawn their request for quote.", {
            ns: "ProjectOverview",
          })}
        </Alert>
        <ConfirmProcess ref={confirmProcessRef} />
      </>
    );
  }

  if (request.status === "CLOSED") {
    return (
      <>
        <Alert
          variant="outlined"
          severity="warning"
          action={
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={async () => {
                await handleStateChange("CONTRACTEE_DECLINE");
              }}
            >
              {t("Close opportunity", { ns: "ProjectOverview" })}
            </Button>
          }
        >
          {t("The customer's request for quote has been closed.", {
            ns: "ProjectOverview",
          })}
        </Alert>
        <ConfirmProcess ref={confirmProcessRef} />
      </>
    );
  }

  if (request.status === "CLOSED_FOR_BIDDING") {
    return (
      <>
        <Alert
          variant="outlined"
          severity="warning"
          action={
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={async () => {
                await handleStateChange(
                  havePublishedQuotes
                    ? "CONTRACTOR_DECLINE"
                    : "CONTRACTEE_DECLINE"
                );
              }}
            >
              {t("Close opportunity", { ns: "ProjectOverview" })}
            </Button>
          }
        >
          {t("The customer is no longer accepting new quotes.", {
            ns: "ProjectOverview",
          })}
        </Alert>
        <ConfirmProcess ref={confirmProcessRef} />
      </>
    );
  }

  return null;
};
